import { Navigate, Outlet } from "react-router-dom";

import { CenteredMessage } from "~/components/ui/centeredMessage/CenteredMessage";
import { useAuth } from "~/contexts/AuthProvider";
import { useTeam } from "~/contexts/TeamProvider";
import { routes } from "~/router";

import Spinner from "../ui/spinner/Spinner";
import {
  getRequiredOnboardingStep,
  onboardingStepPath,
} from "./onboardingStep";

const SubscriptionRequiredRoute = () => {
  const { user } = useAuth();
  const { teamId, team, status, isFetching, isLoading } = useTeam();

  /**
   * teamId is derived from user.current_team_id (binding),
   * so:
   * - status === "idle" means "no team selected"
   * - status === "pending" means "loading team"
   */

  // No selected team → go select team
  // (this will also cover status === "idle")
  if (!teamId) {
    return <Navigate to={routes.selectTeam()} replace />;
  }

  // Team is being loaded (first load or a team switch) → block UI (do NOT
  // redirect). Background refetches of an already loaded team keep the page:
  // the blocked billing screen polls the team, and a spinner would flash.
  if (status === "pending" || isLoading || (isFetching && !team)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  // Team fetch resolved but team is still null (or status error) → something wrong (team deleted / access revoked)
  // safest behavior: force select team again
  if ((status === "success" && !team) || status === "error") {
    return <Navigate to={routes.selectTeam()} replace />;
  }

  // White-label clients of an Autopto agency without a card get 403 on
  // everything but the profile, the team and notifications. Billing is
  // closed to clients altogether (their features turn it off): sending them
  // there on an unusable subscription would bounce them home in a loop, and
  // the agency's payment state isn't theirs to see.
  if (
    user?.tenant_account &&
    (team?.autopto_card_required || !team?.subscription?.healthy)
  ) {
    return (
      <CenteredMessage
        title="Access is temporarily unavailable"
        description="Please contact your agency to restore access."
      />
    );
  }

  // The backend decides whether the team can use the app (status and the
  // Autopto trial block alike).
  if (team?.subscription?.healthy) {
    const onboardingStep = getRequiredOnboardingStep(team);

    if (onboardingStep) {
      return <Navigate to={onboardingStepPath(onboardingStep)} replace />;
    }

    return <Outlet />;
  }

  // No usable subscription → billing
  return <Navigate to={routes.billing.subscription()} replace />;
};

export default SubscriptionRequiredRoute;
